import React, { useState } from 'react';

import { useCurrentuser } from '@uplift/contexts';
import { useFueCompleted } from '@uplift/hooks';
import { useProviderScheduleQuery } from '@uplift/queries';
import { Button, ContextualNavigationLayout, ProviderLayout, SuggestDesktopModal, UpliftThemeProvider } from '@uplift/ui-v2';

import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CalendarContent } from './calendar-content';
import { ScheduleConfigurationContent } from './schedule-configuration-content';
import { ArrowBack } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

export const SchedulePage: React.FC = () => {
  const [ fueStep, setFueState ] = useState(null);
  const me = useCurrentuser();
  const { data, isLoading } = useProviderScheduleQuery(me.id);
  const { fueCompleted, isFetching } = useFueCompleted(350);
  const isProviderMSOPartner = me.rules?.msoPartner;
  const { classes } = useStyles();
  const history = useHistory();

  const handleStateUpdate = (newState: string) => {
    setFueState(newState);
  };

  const showBackButton = React.useMemo(() => fueStep !== 'calendarIntegration' && <Button startIcon={<ArrowBack />} onClick={() => history.goBack()}>Back to Dashboard</Button>, [ fueStep, history ]);

  const content = React.useMemo(() => {
    if (isProviderMSOPartner && !fueCompleted) {
      return <CalendarContent />;
    }

    if (fueCompleted === false) {
      return (
        <>
          <Box className={classes.navBar}>
            <ContextualNavigationLayout
              headerProps={{
                leftButton: showBackButton,
                centerBlock: <Typography variant="h6" className={classes.centerBlockHeader}>Setup Matching Availability</Typography>
              }}
            >
              <ScheduleConfigurationContent idealCaseload={data?.idealCaseload} onStateUpdate={handleStateUpdate} />
            </ContextualNavigationLayout>
          </Box>
          <SuggestDesktopModal />
        </>
      );
    }

    if (fueCompleted) {
      return <CalendarContent />;
    }

    return null;
  }, [ isProviderMSOPartner, fueCompleted, classes.navBar, classes.centerBlockHeader, showBackButton, data?.idealCaseload ]);

  return (
    <ProviderLayout loading={isFetching || isLoading} background="white">
      <UpliftThemeProvider>
        {content}
      </UpliftThemeProvider>
    </ProviderLayout>
  );
};

const useStyles = makeStyles()(theme => ({
  centerBlockHeader: {
    color: theme.palette.darkSlate.main,
    textAlign: 'center',
  },
  navBar: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    padding: theme.spacing(0, 1),
    zIndex: theme.zIndex.appBar + 2,
    backgroundColor: theme.palette.background.paper,
    top: 0,
    paddingBottom: theme.spacing(5),
  },
}));
